import {
  type DataProviderSet,
  DataType,
  isBoxBreakdownDataSet,
} from '../../../DataProvider/types';
import { convertAreaDataToSheet } from './converters/area-data-converter';
import { convertBreakdownBoxDataToSheet } from './converters/breakdown-box-data-converter';
import { convertBreakdownSingleDataToSheet } from './converters/breakdown-single-data-converter';
import { convertDistributionDataToSheet } from './converters/distribution-data-converter';
import { convertMixedStatsDataToSheet } from './converters/mixed-stats-data-converter';
import { convertShareOfSegmentData } from './converters/share-of-segment-converter';
import { convertSingleDataToSheet } from './converters/single-data-converter';
import { convertTableDataToSheet } from './converters/table-data-converter';
import { convertTimeseriesDataToSheet } from './converters/timeseries-data-converter';
import { convertVolumeDataToSheet } from './converters/volume-data-converter';
import type { DataDownloadResult } from './types';

export type DataToSheetConverter = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
  data: DataProviderSet | any,
  primaryDimension: string,
  secondaryDimension: string,
  metric: string
) => DataDownloadResult;

export const COMPETITOR_BAR_DATA_TYPE = 'competitor-bar';
export const SENTIMENT_EFFECT_BAR_DATA_TYPE = 'sentiment-effect-bar';
export const ENTITY_SUMMARY_DATA_TYPE = 'entitySummaryData';
export const GAUGE_DATA_TYPE = 'gaugeData';
export const OVERVIEW_MAP_DATA_TYPE = 'overviewMapData';
export const MIXED_STATS_DATA_TYPE = 'mixedStatsData';
export const SHARE_OF_SEGMENT_DATA_TYPE = 'share-of-segment';
export const AREA_CHART_DATA_TYPE = 'area-chart';
export const VOLUME_CHART_DATA_TYPE = 'volume-chart';

export const dataDownloadRegistry: Record<string, DataToSheetConverter> = {
  // Generic chart data converters
  [DataType.TABLE_ROWS]: (data) => {
    const rows = (
      data as unknown as {
        type: DataType.TABLE_ROWS;
        data: Array<Record<string, unknown>>;
      }
    ).data;
    const sheetData = convertTableDataToSheet(rows);
    return { sheetData, skipHeader: false };
  },
  [DataType.SINGLE]: (data, primaryDimension, secondaryDimension, metric) => ({
    sheetData: convertSingleDataToSheet({
      data,
      primaryDimension,
      secondaryDimension,
      metric,
    }),
    skipHeader: false,
  }),

  [DataType.TIMESERIES]: (
    data,
    primaryDimension,
    secondaryDimension,
    metric
  ) => ({
    sheetData: convertTimeseriesDataToSheet({
      data,
      primaryDimension,
      secondaryDimension,
      metric,
    }),
    skipHeader: false,
  }),

  [DataType.BREAKDOWN_SINGLE]: (
    data,
    primaryDimension,
    secondaryDimension,
    metric
  ) => ({
    sheetData: convertBreakdownSingleDataToSheet({
      data,
      primaryDimension,
      secondaryDimension,
      metric,
    }),
    skipHeader: true,
  }),

  [DataType.DISTRIBUTION]: (
    data,
    primaryDimension,
    secondaryDimension,
    metric
  ) => ({
    sheetData: convertDistributionDataToSheet({
      data,
      primaryDimension,
      secondaryDimension,
      metric,
    }),
    skipHeader: false,
  }),

  [DataType.BOX]: (data, primaryDimension, secondaryDimension, metric) => {
    if (!isBoxBreakdownDataSet(data)) {
      return { sheetData: [], skipHeader: true };
    }
    return {
      sheetData: convertBreakdownBoxDataToSheet({
        data,
        primaryDimension,
        secondaryDimension,
        metric,
      }),
      skipHeader: false,
    };
  },

  // Special charts
  [COMPETITOR_BAR_DATA_TYPE]: (
    data,
    primaryDimension,
    secondaryDimension,
    metric
  ) => ({
    sheetData: convertSingleDataToSheet({
      data,
      primaryDimension,
      secondaryDimension,
      metric,
    }),
    skipHeader: false,
  }),

  [SENTIMENT_EFFECT_BAR_DATA_TYPE]: (
    data,
    primaryDimension,
    secondaryDimension,
    metric
  ) => ({
    sheetData: convertSingleDataToSheet({
      data,
      primaryDimension,
      secondaryDimension,
      metric,
    }),
    skipHeader: false,
  }),

  [SHARE_OF_SEGMENT_DATA_TYPE]: (
    data,
    primaryDimension,
    secondaryDimension,
    metric
  ) => ({
    sheetData: convertShareOfSegmentData({
      data,
      primaryDimension,
      secondaryDimension,
      metric,
    }),
    skipHeader: true,
  }),

  [AREA_CHART_DATA_TYPE]: (
    data,
    primaryDimension,
    secondaryDimension,
    metric
  ) => ({
    sheetData: convertAreaDataToSheet({
      data,
      primaryDimension,
      secondaryDimension,
      metric,
    }),
    skipHeader: false,
  }),

  [VOLUME_CHART_DATA_TYPE]: (
    data,
    primaryDimension,
    secondaryDimension,
    metric
  ) => ({
    sheetData: convertVolumeDataToSheet({
      data,
      primaryDimension,
      secondaryDimension,
      metric,
    }),
    skipHeader: false,
  }),

  [MIXED_STATS_DATA_TYPE]: (
    data,
    primaryDimension,
    secondaryDimension,
    metric
  ) => ({
    sheetData: convertMixedStatsDataToSheet({
      data: data as unknown as Parameters<
        typeof convertMixedStatsDataToSheet
      >[0]['data'],
      primaryDimension,
      secondaryDimension,
      metric,
    }),
    skipHeader: false,
  }),
};
